import { type ConsentState } from '../../typings'
import { BaseCMP } from './base-cmp'

export class CookieBotCMP extends BaseCMP {
  private cookiebotListeners: (() => void)[] = []
  private listenersInitialized = false

  initialize(): void {
    if (this.initialized) {
      return
    }

    const managementMode = this.getManagementMode()

    if (managementMode === 'managed') {
      this.installCookieBot()
    } else if (managementMode === 'self') {
      this.log('CookieBot assumed to be already installed, setting up listeners')
      this.setupEventListeners()
    }

    this.initialized = true
  }

  private installCookieBot(): void {
    if (!this.config.cmpId) {
      throw new Error('CookieBot container ID is required for installation')
    }

    const domain = window.location.hostname
    if (domain === 'localhost' || domain === '127.0.0.1') {
      this.log('Running on localhost - ensure domain is configured in CookieBot dashboard')
    }

    const loadCookieBot = () => {
      if (!document.getElementById('Cookiebot')) {
        const script = document.createElement('script')
        script.id = 'Cookiebot'
        script.src = 'https://consent.cookiebot.com/uc.js'
        script.setAttribute('data-cbid', this.config.cmpId!)
        script.setAttribute('data-blockingmode', 'auto')
        script.setAttribute('data-track-enabled', 'false')
        script.type = 'text/javascript'
        script.async = true

        script.onerror = (e) => {
          this.logError('Failed to load CookieBot script', e)
        }

        document.head.appendChild(script)
      }

      this.setupEventListeners()
    }

    // Load now rather than on `load`: the consent answer gates every tag, and
    // `wait_for_update` is only 500ms. Waiting for every image to finish means a
    // returning visitor who already consented still gets a `denied` first hit.
    loadCookieBot()
  }

  protected setupEventListeners(): void {
    if (this.listenersInitialized) return

    if (window.Cookiebot?.consent) {
      this.handleConsentUpdate()
    } else {
      window.addEventListener('CookiebotOnConsentReady', this.handleCookiebot)
      window.addEventListener('CookiebotOnAccept', this.handleCookiebot)
      window.addEventListener('CookiebotOnDecline', this.handleCookiebot)
      this.cookiebotListeners.push(this.handleCookiebot)
    }

    this.listenersInitialized = true
  }

  private handleCookiebot = () => {
    this.handleConsentUpdate()
  }

  protected handleConsentUpdate(): void {
    if (window.Cookiebot && this.consentUpdateCallback) {
      const consentState: ConsentState = {
        analytics: window.Cookiebot.consent.statistics,
        marketing: window.Cookiebot.consent.marketing,
        preferences: window.Cookiebot.consent.preferences,
        client_timestamp: Date.now()
      }
      this.consentUpdateCallback(consentState)
    }
  }

  getConsentState(): ConsentState | null {
    if (!window.Cookiebot?.consent) {
      return null
    }

    return {
      analytics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
      preferences: window.Cookiebot.consent.preferences,
      client_timestamp: Date.now()
    }
  }

  destroy(): void {
    this.cookiebotListeners.forEach((listener) => {
      window.removeEventListener('CookiebotOnConsentReady', listener)
      window.removeEventListener('CookiebotOnAccept', listener)
      window.removeEventListener('CookiebotOnDecline', listener)
    })

    this.cookiebotListeners = []
    this.listenersInitialized = false
    this.initialized = false
    this.consentUpdateCallback = undefined

    this.log('Destroyed CookieBot CMP')
  }

  public loadCookieDeclaration(targetElementId: string): void {
    if (!document.getElementById('CookieDeclaration')) {
      const script = document.createElement('script')
      script.id = 'CookieDeclaration'
      script.src = `https://consent.cookiebot.com/${this.config.cmpId}/cd.js`
      script.async = true

      script.onerror = () => {
        this.logError('Failed to load Cookie Declaration script')
      }

      const targetElement = document.getElementById(targetElementId)
      if (targetElement) {
        targetElement.appendChild(script)
      } else {
        this.log(`Target element with id '${targetElementId}' not found`)
      }
    }
  }
}
